import { useRef, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { Navigate } from 'react-router-dom'

import { AdjustPreviewModal } from './components/modals/AdjustPreviewModal'
import { BonusModal } from '@pages/BonusModal'
import { BigNumbersFilters } from './components/BigNumbersFilters'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { consolidatedPreview } from './components/ConsolidatedPreview'
import { DetailedPreviewFilters } from './components/DetailedPreviewFilters'
import { emotionTheme } from '@styles/theme'
import { initialPagination } from '@components/shared/Table/components/Pagination/types'
import { BlockDeviationModal } from './components/modals/BlockDeviationModal'
import { JustifyingDeviationsModal } from './components/modals/JustifyingDeviationsModal'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseDetailedPreviewGrid } from './helpers/parseDetailedPreviewGrid'
import { hasPreviewItemFlag } from './helpers/hasPreviewItemFlag'
import { getNextValidationHierarchy, getReturnHierarchy, PROFILES_WITHOUT_ADJUST_BUTTONS } from './helpers/previewGridValidationActions'
import { PreviewBarChart } from './components/PreviewBarChart'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnModal } from '@components/modals/ReturnModal'
import { ReturnPreviousModal } from './components/modals/ReturnPreviousModal'
import { ReturnPreviousSelectedModal } from './components/modals/ReturnPreviousSelectedModal'
import { SeeAdjustPreviewModal } from './components/modals/SeeAdjustPreviewModal'
import { SeeReturnDetailsModal } from './components/modals/SeeReturnDetailsModal'
import { Select } from '@components/shared/Select'
import { SeparatorWithLine } from '@components/shared/SeparatorWithLine'
import { Slider } from '@components/shared/Slider'
import { WarningBanner } from '@components/shared/WarningBanner'
import { TableGrouped } from '@components/shared/TableGrouped'
import { Title } from '@components/shared/Title'
import { useMediaQuery } from '@mui/material'
import { usePreview } from './hooks/usePreview'
import { usePreview as usePreviewContext } from './context'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { currencyMask } from '@utils/masks/currencyMask'
import { Tooltip } from '@components/shared/Tooltip'
import { usePermission } from '@hooks/usePermission'
import { useUser } from '@hooks/useUser'
import { PageIds } from '@hooks/useLayout/types'
import {
  ArrowUUpLeftIcon,
  BinocularsIcon,
  CalculatorIcon,
  CalendarCheckIcon,
  CheckIcon,
  ChecksIcon,
  DownloadSimpleIcon,
  PaperPlaneTiltIcon
} from '@phosphor-icons/react'

import * as S from './styles'
import { toast } from '@utils/functions/notificationToast'

export const Preview = () => {
  const pendingItemRef = useRef<any>(null)
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const [sendReasonId, setSendReasonId] = useState<number | null>(null)
  const [sendSelectedReasonId, setSendSelectedReasonId] = useState<number | null>(null)

  const { filiData, userPermissionsData, isUserPermissionsLoading } = useUser()
  const { canView } = usePermission(PageIds.PREVIEW)

  const profileName = userPermissionsData?.profile?.name
  const nextValidationHierarchy = getNextValidationHierarchy(profileName) || 'próxima hierarquia'
  const returnHierarchy = getReturnHierarchy(profileName) || 'hierarquia anterior'
  const { isDownloadPreviewLoading } = usePreviewContext()

  const {
    methods,
    consolidatedPreviewDataData,
    bigNumbersFiltersData,
    bigNumbersFiltersLoading,
    onConsolidatedPreviewFilters,
    consolidatedPreviewFiltersData,
    consolidatedPreviewFiltersLoading,
    previewDeadlineData,
    previewDeadlineLoading,
    sendReasonsData,
    sendReasonsLoading,
    consolidatedPreviewDetailsData,
    consolidatedPreviewDetailsLoading,
    selectedConsolidatedPreviewIds,
    onSelectConsolidatedPreviewId,
    selectedAllConsolidatedPreviewIds,
    setSelectedAllConsolidatedPreviewIds,
    onConsolidatedPreviewDetailsFilters,
    setConsolidatedPreviewGridFilters,
    getInitialBigNumbersFilters,
    getInitialConsolidatedPreviewFilters,
    onExportConsolidatedPreview,
    isOpenConfirmExportPreview,
    setIsOpenConfirmExportPreview,
    exportConsolidatedPreviewLoading,
    onClearConsolidatedPreviewFilters,
    onClearBigNumbersFilters,
    hasConsolidatedPreviewDetails,
    hasSelection,
    bulkActionFlags,
    approveSelectedIsOpen,
    setApproveSelectedIsOpen,
    validateSelectedIsOpen,
    setValidateSelectedIsOpen,
    sendSelectedIsOpen,
    setSendSelectedIsOpen,
    returnPreviousIsOpen,
    setReturnPreviousIsOpen,
    adjustPreviewIsOpen,
    setAdjustPreviewIsOpen,
    returnPreviousSelectedIsOpen,
    setReturnPreviousSelectedIsOpen,
    seeAdjustPreviewIsOpen,
    setSeeAdjustPreviewIsOpen,
    sendPreviewIsOpen,
    setSendPreviewIsOpen,
    validatePreviewIsOpen,
    setValidatePreviewIsOpen,
    approvePreviewIsOpen,
    setApprovePreviewIsOpen,
    permissions,
    justifyingDeviationsIsOpen,
    setJustifyingDeviationsIsOpen,
    blockDeviationIsOpen,
    setBlockDeviationIsOpen,
    seeReturnDetailsIsOpen,
    setSeeReturnDetailsIsOpen,
    bonusModalIsOpen,
    setBonusModalIsOpen,
    periodSelectedData,
    refetchConsolidatedPreviewDetails,
    adjustPreviewListData,
    adjustPreviewListLoading,
    saveNewAdjustPreview,
    openDeleteAdjustPreviewModal,
    setOpenDeleteAdjustPreviewModal,
    deleteAdjustPreviewLoading,
    deleteAdjustPreviewById,
    saveAdjustPreviewLoading,
    openAdjustPreviewModal,
    setOpenAdjustPreviewModal,
    userData,
    onSeeMorePreview,
    isSendPreviewLoading,
    sendPreview,
    isValidatePreviewLoading,
    validatePreview,
    isApprovePreviewLoading,
    approvePreview,
    isSendPreviewSelectedLoading,
    sendPreviewSelected,
    isValidatePreviewSelectedLoading,
    validatePreviewAllSelected,
    isApprovePreviewSelectedLoading,
    approvePreviewAllSelected,
    isCheckingBulkReturnDraftsLoading,
    checkBulkReturnPreviewDrafts,
    isSavingBulkReturnDraftLoading,
    saveDraftReturnPreviewBulk,
    isSubmittingBulkReturnDraftLoading,
    submitReturnPreviewBulk,
    blockDeviationMessage
  } = usePreview()

  const canEditPage = permissions?.canEdit === true
  /**
   * Visibilidade dos botões da página: vem das flags de permissão que /api/previa/busca-previa-consolidada
   * devolve em cada item (as roles agora são personalizadas, então nada mais é decidido por nome de
   * perfil). Cada botão aparece se alguma linha retornada permite a ação; o `disabled` das ações em
   * massa continua olhando só as linhas selecionadas, via `bulkActionFlags`.
   */
  const previewItems = consolidatedPreviewDetailsData?.items
  const canSubmitPermission = hasPreviewItemFlag(previewItems, (item) => item.canSubmit)
  const canValidatePermission = hasPreviewItemFlag(previewItems, (item) => item.canValidate)
  const canApprovePermission = hasPreviewItemFlag(previewItems, (item) => item.canApprove)
  const canReturnPermission = hasPreviewItemFlag(previewItems, (item) => item.canReturn)
  const canAdjustPermission =
    !PROFILES_WITHOUT_ADJUST_BUTTONS.includes(profileName ?? '') &&
    hasPreviewItemFlag(previewItems, (item) => item.canAdjust)

  /** RN02/RN05: perfis ≠ GU precisam selecionar um motivo antes de enviar a prévia. */
  const requiresSendReason = !!consolidatedPreviewDetailsData?.requiresSendReason
  const sendReasonOptions = (sendReasonsData || []).map((reason) => ({ value: reason.id, label: reason.name }))

  if (!isUserPermissionsLoading && userPermissionsData && !canView) {
    return <Navigate to="/acesso-negado" replace />
  }

  return (
    <>
      <PageWrapper
        breadcrumbData={[
          { label: 'Gestão Financeira' },
          {
            label: `Prévia / ${filiData?.filiCode === 'ALL' ? 'Todos' : `${filiData?.filiCode} - ${filiData?.filiName}`}`
          }
        ]}
        loading={isUserPermissionsLoading}
      >
        <FormProvider {...methods}>
          <BigNumbersFilters
            bigNumbersFiltersData={bigNumbersFiltersData}
            bigNumbersFiltersLoading={bigNumbersFiltersLoading}
            onConsolidatedPreviewFilters={onConsolidatedPreviewFilters}
            getInitialBigNumbersFilters={getInitialBigNumbersFilters}
            onClearConsolidatedPreviewFilters={onClearBigNumbersFilters}
          />

          <SeparatorWithLine />

          <Title label="Consolidado Prévia" marginBottom={32} marginTop={32} />

          <BudgetPreviewsWrapper>
            {isMobile ? (
              <Slider items={consolidatedPreview({ data: consolidatedPreviewDataData?.bigNumbers || null })} />
            ) : (
              <PreviewWrapper>
                {consolidatedPreview({ data: consolidatedPreviewDataData?.bigNumbers || null })}
              </PreviewWrapper>
            )}
          </BudgetPreviewsWrapper>

          <Title label="Evolução Semanal das Prévias por Conta vs Budget" marginBottom={48} />

          <PreviewBarChart data={consolidatedPreviewDataData?.chartData || null} />

          <SeparatorWithLine style={{ marginTop: 50 }} />

          <Title
            label="Detalhamento Prévia Consolidada"
            marginBottom={32}
            marginTop={32}
            action={
              canEditPage && (
                <Tooltip
                  placement="top"
                  title={!hasConsolidatedPreviewDetails ? 'Não há dados para serem exportados.' : ''}
                >
                  <Button
                    colorType="outlineBlue"
                    rightIcon={<DownloadSimpleIcon size={30} />}
                    onClick={() => setIsOpenConfirmExportPreview(true)}
                    disabled={isDownloadPreviewLoading || !hasConsolidatedPreviewDetails}
                  >
                    Exportar
                  </Button>
                </Tooltip>
              )
            }
          />

          <DetailedPreviewFilters
            consolidatedPreviewFiltersData={consolidatedPreviewFiltersData}
            consolidatedPreviewFiltersLoading={consolidatedPreviewFiltersLoading}
            onConsolidatedPreviewDetailsFilters={onConsolidatedPreviewDetailsFilters}
            getInitialConsolidatedPreviewFilters={getInitialConsolidatedPreviewFilters}
            onClearConsolidatedPreviewFilters={onClearConsolidatedPreviewFilters}
          />

          {canEditPage && (
            <>
              <S.NotificationMessage>
                <CalendarCheckIcon size={30} />
                {previewDeadlineLoading
                  ? ' Carregando prazo...'
                  : previewDeadlineData?.deadlineMessage ||
                    previewDeadlineData?.message ||
                    'Você tem até terça-feira 11:59 para enviar a prévia.'}
              </S.NotificationMessage>

              <S.ActionButtons>
                {canValidatePermission && (
                  <Button
                    width={isMobile ? '100%' : 250}
                    colorType="solidBlue"
                    rightIcon={<CheckIcon size={30} />}
                    onClick={() => {
                      if (selectedConsolidatedPreviewIds.length || selectedAllConsolidatedPreviewIds) {
                        setValidateSelectedIsOpen(true)
                      } else {
                        toast.warning('Nenhuma prévia selecionada para validação.')
                      }
                    }}
                    style={{ background: 'rgba(52, 168, 102, 0.15)', color: '#34A866' }}
                    disabled={!hasSelection || !bulkActionFlags.canValidate}
                  >
                    Validar Selecionadas
                  </Button>
                )}

                {canApprovePermission && (
                  <Button
                    width={isMobile ? '100%' : 250}
                    colorType="solidBlue"
                    rightIcon={<ChecksIcon size={30} />}
                    onClick={() => {
                      if (selectedConsolidatedPreviewIds.length || selectedAllConsolidatedPreviewIds) {
                        setApproveSelectedIsOpen(true)
                      } else {
                        toast.warning('Nenhuma prévia selecionada para aprovação.')
                      }
                    }}
                    style={{ background: '#C6F6D5', color: '#228085' }}
                    disabled={!hasSelection || !bulkActionFlags.canApprove}
                  >
                    Aprovar Selecionadas
                  </Button>
                )}

                {canReturnPermission && (
                  <Button
                    width={isMobile ? '100%' : 250}
                    colorType="solidBlue"
                    rightIcon={<ArrowUUpLeftIcon size={30} />}
                    onClick={() => {
                      if (selectedConsolidatedPreviewIds.length || selectedAllConsolidatedPreviewIds) {
                        setReturnPreviousSelectedIsOpen(true)
                      } else {
                        toast.warning('Nenhuma prévia selecionada para devolução.')
                      }
                    }}
                    style={{ background: 'rgba(238, 0, 0, 0.08)', color: '#E00' }}
                    disabled={!hasSelection || !bulkActionFlags.canReturn}
                  >
                    Devolver Selecionadas
                  </Button>
                )}

                {canSubmitPermission && (
                  <Button
                    width={isMobile ? '100%' : 250}
                    colorType="solidBlue"
                    rightIcon={<PaperPlaneTiltIcon size={30} />}
                    onClick={() => {
                      if (selectedConsolidatedPreviewIds.length || selectedAllConsolidatedPreviewIds) {
                        setSendSelectedIsOpen(true)
                      } else {
                        toast.warning('Nenhuma prévia selecionada para envio.')
                      }
                    }}
                    style={{ background: '#BEE3F8', color: '#2A295C' }}
                    disabled={!hasSelection || !bulkActionFlags.canSubmit}
                  >
                    Enviar Selecionadas
                  </Button>
                )}
              </S.ActionButtons>
            </>
          )}

          <TableGrouped
            data={parseDetailedPreviewGrid({
              data: consolidatedPreviewDetailsData?.items || [],
              onSendPreview: (item) => {
                pendingItemRef.current = item
                setSendPreviewIsOpen(true)
              },
              onValidatePreview: (item) => {
                pendingItemRef.current = item
                setValidatePreviewIsOpen(true)
              },
              onApprovePreview: (item) => {
                pendingItemRef.current = item
                setApprovePreviewIsOpen(true)
              },
              onJustifyDeviation: (item) => {
                pendingItemRef.current = item
                setJustifyingDeviationsIsOpen(true)
              },
              onReturnPreview: (item) => {
                pendingItemRef.current = item
                setReturnPreviousIsOpen(true)
              },
              onSeeMore: (item) => {
                pendingItemRef.current = item
                onSeeMorePreview(item)
              },
              onSeeMoreReturn: (item) => {
                pendingItemRef.current = item
                setSeeReturnDetailsIsOpen(true)
              },
              selectedIds: selectedConsolidatedPreviewIds,
              onToggleCheckbox: onSelectConsolidatedPreviewId,
              selectedAllIds: selectedAllConsolidatedPreviewIds,
              onSelectAll: setSelectedAllConsolidatedPreviewIds,
              canEdit: canEditPage
            })}
            fixedRow={{
              element: (
                <RowTableTotal2
                  data={[
                    { label: 'Total Registros', value: consolidatedPreviewDetailsData?.totalItems ?? 0 },
                    {
                      label: 'GP Budget',
                      value: `${currencyMask(consolidatedPreviewDetailsData?.totalizers?.grossProfitBudget ?? 0)} | GM: ${(consolidatedPreviewDetailsData?.totalizers?.grossMarginBudget ?? 0).toFixed(2).replace('.', ',')}%`
                    },
                    {
                      label: 'GP Prévia Atual',
                      value: `${currencyMask(consolidatedPreviewDetailsData?.totalizers?.grossProfitActualPreview ?? 0)} | GM: ${(consolidatedPreviewDetailsData?.totalizers?.grossMarginActualPreview ?? 0).toFixed(2).replace('.', ',')}%`
                    },
                    {
                      label: 'GP Desvio',
                      value: `${currencyMask(consolidatedPreviewDetailsData?.totalizers?.grossProfitDeviation ?? 0)} | GM: ${(consolidatedPreviewDetailsData?.totalizers?.grossMarginDeviation ?? 0).toFixed(2).replace('.', ',')}%`
                    }
                  ]}
                />
              )
            }}
            footer={{
              pagination: {
                currentPage: consolidatedPreviewDetailsData?.currentPage ?? initialPagination.PAGE,
                itemsPerPage: consolidatedPreviewDetailsData?.itemsPerPage ?? initialPagination.ITEMS_PER_PAGE,
                totalItems: consolidatedPreviewDetailsData?.totalItems ?? 0,
                totalPages: consolidatedPreviewDetailsData?.totalPages ?? 0,
                onPageChange: (page) => setConsolidatedPreviewGridFilters((prev) => ({ ...prev, page })),
                onItemsPerPageChange: (itemsPer) =>
                  setConsolidatedPreviewGridFilters((prev) => ({ ...prev, itemsPerPage: itemsPer }))
              }
            }}
            onSort={(newSortData) =>
              setConsolidatedPreviewGridFilters((prev) => ({
                ...prev,
                sortBy: newSortData?.sortBy,
                sortType: newSortData?.sortType
              }))
            }
            isLoading={consolidatedPreviewDetailsLoading}
          />

          <S.Buttons>
            {canEditPage && canAdjustPermission && (
              <Button
                width={isMobile ? '100%' : 200}
                colorType="solidBlue"
                rightIcon={<CalculatorIcon size={30} />}
                onClick={() => setAdjustPreviewIsOpen(true)}
              >
                Ajustar Prévia
              </Button>
            )}

            {canAdjustPermission && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="outlineBlue"
                rightIcon={<BinocularsIcon size={30} />}
                onClick={() => setSeeAdjustPreviewIsOpen(true)}
              >
                Ver Ajustes
              </Button>
            )}
          </S.Buttons>
        </FormProvider>
      </PageWrapper>

      <ReturnModal
        open={approveSelectedIsOpen}
        headerIcon={<ChecksIcon size={24} weight="bold" />}
        headerTitle="Aprovar Selecionadas"
        message="Tem certeza de que deseja aprovar as Prévias selecionadas?"
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setApproveSelectedIsOpen}
        isLoading={isApprovePreviewSelectedLoading}
        onConfirm={async () => {
          if (!canEditPage) return

          const success = await approvePreviewAllSelected({
            weekNumber: 0,
            referenceDate: periodSelectedData?.competenceDate || ''
          })
          if (success) setApproveSelectedIsOpen(false)
        }}
      />

      <ReturnModal
        open={validateSelectedIsOpen}
        headerIcon={<CheckIcon size={24} weight="bold" />}
        headerTitle="Validar Selecionadas"
        message={`Tem certeza de que deseja validar as Prévias selecionadas e encaminhá-las para validação do(a) ${nextValidationHierarchy}?`}
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Validar"
        onClose={setValidateSelectedIsOpen}
        isLoading={isValidatePreviewSelectedLoading}
        onConfirm={async () => {
          if (!canEditPage) return

          const success = await validatePreviewAllSelected({
            weekNumber: 0,
            referenceDate: periodSelectedData?.competenceDate || ''
          })
          if (success) setValidateSelectedIsOpen(false)
        }}
      />

      <ReturnModal
        open={sendSelectedIsOpen}
        headerIcon={<PaperPlaneTiltIcon size={24} weight="bold" />}
        headerTitle="Enviar Selecionadas"
        message="Tem certeza de que deseja enviar as Prévias selecionadas para validação do(a) GCE/GO?"
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Enviar"
        confirmButtonDisabled={requiresSendReason && !sendSelectedReasonId}
        onClose={() => {
          setSendSelectedIsOpen(false)
          setSendSelectedReasonId(null)
        }}
        isLoading={isSendPreviewSelectedLoading}
        onConfirm={() => {
          if (!canEditPage) return

          sendPreviewSelected({
            weekNumber: 0,
            referenceDate: periodSelectedData?.competenceDate || '',
            sendReasonId: sendSelectedReasonId ?? undefined
          })
          setSendSelectedReasonId(null)
        }}
      >
        {requiresSendReason && (
          <>
            <WarningBanner
              marginTop={0}
              message={
                <>
                  <strong>Perfil diferente de Gerente de Unidade (GU)</strong>
                  <br />
                  Por favor, selecione o motivo do envio antes de prosseguir com a validação.
                </>
              }
            />
            <Select
              hasController={false}
              name="sendSelectedReasonId"
              label="Motivo do envio da prévia"
              placeholder="Selecione o motivo do envio..."
              required
              loading={sendReasonsLoading}
              options={sendReasonOptions}
              value={sendSelectedReasonId ?? ''}
              onChange={(e) => setSendSelectedReasonId(Number(e.target.value))}
              fullWidth
            />
          </>
        )}
      </ReturnModal>

      <ReturnModal
        open={isOpenConfirmExportPreview}
        headerIcon={<DownloadSimpleIcon size={24} weight="bold" />}
        headerTitle="Exportar Prévia"
        message="Tem certeza de que deseja exportar os dados da Prévia Consolidada do período selecionado?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setIsOpenConfirmExportPreview}
        isLoading={exportConsolidatedPreviewLoading}
        onConfirm={onExportConsolidatedPreview}
      />

      <ReturnModal
        open={sendPreviewIsOpen}
        headerIcon={<PaperPlaneTiltIcon size={24} weight="bold" />}
        headerTitle="Enviar Prévia"
        message="Tem certeza de que deseja enviar a Prévia para validação do(a) GCE?"
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Enviar"
        confirmButtonDisabled={requiresSendReason && !sendReasonId}
        onClose={() => {
          setSendPreviewIsOpen(false)
          setSendReasonId(null)
          pendingItemRef.current = null
        }}
        isLoading={isSendPreviewLoading}
        onConfirm={async () => {
          if (!canEditPage) return

          await sendPreview({
            branchCode: pendingItemRef.current?.profitCenter,
            weekNumber: pendingItemRef.current?.weekNumber,
            referenceDate: periodSelectedData?.competenceDate || '',
            sendReasonId: sendReasonId ?? undefined
          })
          setSendReasonId(null)
          pendingItemRef.current = null
        }}
      >
        {requiresSendReason && (
          <>
            <WarningBanner
              marginTop={0}
              message={
                <>
                  <strong>Perfil diferente de Gerente de Unidade (GU)</strong>
                  <br />
                  Por favor, selecione o motivo do envio antes de prosseguir com a validação.
                </>
              }
            />
            <Select
              hasController={false}
              name="sendReasonId"
              label="Motivo do envio da prévia"
              placeholder="Selecione o motivo do envio..."
              required
              loading={sendReasonsLoading}
              options={sendReasonOptions}
              value={sendReasonId ?? ''}
              onChange={(e) => setSendReasonId(Number(e.target.value))}
              fullWidth
            />
          </>
        )}
      </ReturnModal>

      <ReturnModal
        open={validatePreviewIsOpen}
        headerIcon={<CheckIcon size={24} weight="bold" />}
        headerTitle="Validar Prévia"
        message={`Tem certeza de que deseja validar a Prévia e encaminhá-la para validação do(a) ${nextValidationHierarchy}?`}
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Enviar"
        onClose={() => {
          setValidatePreviewIsOpen(false)
          pendingItemRef.current = null
        }}
        isLoading={isValidatePreviewLoading}
        onConfirm={async () => {
          if (!canEditPage) return

          const success = await validatePreview({
            branchCode: pendingItemRef.current?.profitCenter,
            weekNumber: pendingItemRef.current?.weekNumber,
            referenceDate: periodSelectedData?.competenceDate || ''
          })
          if (success) {
            setValidatePreviewIsOpen(false)
            pendingItemRef.current = null
          }
        }}
      />

      <ReturnModal
        open={approvePreviewIsOpen}
        headerIcon={<ChecksIcon size={24} weight="bold" />}
        headerTitle="Aprovar Prévia"
        message="Tem certeza de que deseja aprovar a Prévia?"
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Enviar"
        onClose={() => {
          setApprovePreviewIsOpen(false)
          pendingItemRef.current = null
        }}
        isLoading={isApprovePreviewLoading}
        onConfirm={async () => {
          if (!canEditPage) return

          const success = await approvePreview({
            branchCode: pendingItemRef.current?.profitCenter,
            weekNumber: pendingItemRef.current?.weekNumber,
            referenceDate: periodSelectedData?.competenceDate || ''
          })
          if (success) {
            setApprovePreviewIsOpen(false)
            pendingItemRef.current = null
          }
        }}
      />

      <ReturnPreviousSelectedModal
        open={returnPreviousSelectedIsOpen}
        onClose={setReturnPreviousSelectedIsOpen}
        accountOptions={consolidatedPreviewFiltersData?.account}
        returnHierarchy={returnHierarchy}
        isCheckingDraftsLoading={isCheckingBulkReturnDraftsLoading}
        isSavingDraftLoading={isSavingBulkReturnDraftLoading}
        isSubmittingLoading={isSubmittingBulkReturnDraftLoading}
        onCheckDrafts={checkBulkReturnPreviewDrafts}
        onSaveDraft={canEditPage ? saveDraftReturnPreviewBulk : undefined}
        onSubmitBulk={canEditPage ? submitReturnPreviewBulk : undefined}
      />

      <ReturnPreviousModal
        data={pendingItemRef.current}
        open={returnPreviousIsOpen}
        onClose={setReturnPreviousIsOpen}
        returnHierarchy={returnHierarchy}
        onSubmitSuccess={refetchConsolidatedPreviewDetails}
      />

      <AdjustPreviewModal
        open={adjustPreviewIsOpen}
        userData={userData}
        branchCode={filiData?.filiCode ?? ''}
        periodSelectedData={periodSelectedData}
        adjustPreviewListData={adjustPreviewListData}
        adjustPreviewListLoading={adjustPreviewListLoading}
        consolidatedPreviewFiltersData={consolidatedPreviewFiltersData}
        saveNewAdjustPreview={saveNewAdjustPreview}
        openDeleteAdjustPreviewModal={openDeleteAdjustPreviewModal}
        setOpenDeleteAdjustPreviewModal={setOpenDeleteAdjustPreviewModal}
        deleteAdjustPreviewLoading={deleteAdjustPreviewLoading}
        deleteAdjustPreviewById={deleteAdjustPreviewById}
        saveAdjustPreviewLoading={saveAdjustPreviewLoading}
        openAdjustPreviewModal={openAdjustPreviewModal}
        setOpenAdjustPreviewModal={setOpenAdjustPreviewModal}
        userCanEdit={canEditPage}
        onClose={setAdjustPreviewIsOpen}
      />

      <SeeAdjustPreviewModal
        open={seeAdjustPreviewIsOpen}
        onClose={setSeeAdjustPreviewIsOpen}
        userData={userData}
        periodSelectedData={periodSelectedData}
        adjustPreviewListData={adjustPreviewListData}
        adjustPreviewListLoading={adjustPreviewListLoading}
        consolidatedPreviewFiltersData={consolidatedPreviewFiltersData}
      />

      <JustifyingDeviationsModal
        data={pendingItemRef.current}
        open={justifyingDeviationsIsOpen}
        onClose={setJustifyingDeviationsIsOpen}
        userCanEdit={canEditPage}
        currentUserId={userPermissionsData?.id.toString()}
        branchCode={pendingItemRef.current?.profitCenter}
        economicGroupCode={pendingItemRef.current?.economicGroupCode}
        accountId={pendingItemRef.current?.accountId}
        referenceDate={periodSelectedData?.competenceDate ?? undefined}
        onSave={async () => {
          await refetchConsolidatedPreviewDetails()
          setJustifyingDeviationsIsOpen(false)
        }}
      />

      <BlockDeviationModal
        open={blockDeviationIsOpen}
        onClose={setBlockDeviationIsOpen}
        message={blockDeviationMessage}
      />

      <SeeReturnDetailsModal
        data={pendingItemRef.current}
        open={seeReturnDetailsIsOpen}
        onClose={setSeeReturnDetailsIsOpen}
      />

      <BonusModal open={bonusModalIsOpen} onClose={() => setBonusModalIsOpen(false)} />
    </>
  )
}
